import { useEffect, useState, type FormEvent } from "react";
import AccountLayout, { FormMessage, SettingsSection } from "../components/AccountLayout";
import { fieldLabel } from "@/components/ui/kit";
import { identityApi, type IdentityProfile } from "@/api/identity";
import { reloadIdentity } from "@/api/session";

const field = "flex min-w-0 flex-col gap-1.5";

export default function AccountProfile() {
  const [profile, setProfile] = useState<IdentityProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const show = (p: IdentityProfile) => {
    setProfile(p);
    setName(p.name);
  };

  useEffect(() => {
    identityApi<IdentityProfile>("GET", "/api/auth/me")
      .then(show)
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : "Couldn't load your profile."));
  }, []);

  const dirty = !!profile && name.trim() !== profile.name;

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!name.trim()) {
      setError("Enter a name.");
      return;
    }
    setSaving(true);
    try {
      show(
        // Only the name: the SSO leaves fields it isn't sent (phone, locale, timezone) as they are.
        await identityApi<IdentityProfile>("PATCH", "/api/auth/profile", { name: name.trim() })
      );
      // The name in the top bar and the greeting come from the session.
      await reloadIdentity().catch(() => {});
      setSuccess("Profile saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AccountLayout>
      <SettingsSection title="Personal details" description="Your name appears across TradeStorm. Email is set at signup and can't be changed here.">
        {loadError ? (
          <FormMessage kind="error">{loadError}</FormMessage>
        ) : !profile ? (
          <p className="m-0 text-sm text-[var(--text-muted)]">Loading…</p>
        ) : (
          <form onSubmit={save} className="flex flex-col gap-4">
            {error && <FormMessage kind="error">{error}</FormMessage>}
            {success && <FormMessage kind="success">{success}</FormMessage>}
            <label className={field}>
              <span className={fieldLabel}>Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={saving} autoComplete="name" />
            </label>
            <label className={field}>
              <span className={fieldLabel}>Email</span>
              <input value={profile.email} disabled />
            </label>
            <div className="flex justify-end">
              <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        )}
      </SettingsSection>
    </AccountLayout>
  );
}
